import { Link } from 'react-router-dom';
import { useProspects } from '@/hooks/useProspects';
import { ProspectProgressBar } from '@/components/ProspectProgressBar';
import { PROSPECT_TOUCH_TYPES, prospectProgress } from '../../shared/prospect-engagement';

// The prospect tracker bar on his People profile, so Scott sees touch progress
// without opening Prospects. Shows nothing for a player with no prospect profile.
export function ProspectTouchPanel({ playerId }: { playerId: string }) {
  const prospects = useProspects('prospect');
  const clients = useProspects('client');
  const match = (data: typeof prospects.data) =>
    data?.profiles.find((row) => row.status === 'active' && (row.playerId === playerId || row.linkedPlayerId === playerId));
  const clientProfile = match(clients.data);
  const profile = clientProfile ?? match(prospects.data);
  if (!profile) return null;
  const isClient = Boolean(clientProfile);
  const source = isClient ? clients.data : prospects.data;

  const touches = (source?.touches ?? []).filter((touch) => touch.prospectId === profile.id);
  const progress = prospectProgress(
    touches.map((touch) => touch.touchType),
    profile.priorityGroup
  );
  const counts = PROSPECT_TOUCH_TYPES.map((type) => ({
    ...type,
    count: touches.filter((touch) => touch.touchType === type.key).length,
  })).filter((type) => type.count > 0);
  const latest = touches
    .map((touch) => touch.occurredAt)
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => b.localeCompare(a))[0];

  return (
    <section className="surface-soft rounded-control p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
          {isClient ? 'Client touches' : 'Prospect touches'}
          {profile.priorityGroup != null && ` · P${profile.priorityGroup}`}
        </h2>
        <Link to={isClient ? '/clients' : '/prospects'} className="font-mono text-[11px] text-muted hover:text-ink">
          Log touches →
        </Link>
      </div>
      <ProspectProgressBar
        earned={progress.earned}
        goal={progress.goal}
        progressPercent={progress.progressPercent}
        pace={progress.pace}
        size="md"
      />
      <p className="mt-2 text-xs text-muted">
        {counts.length === 0
          ? 'No touches logged yet.'
          : counts.map((type) => `${type.label} ×${type.count}`).join(' · ')}
        {latest && ` · Latest ${latest}`}
      </p>
    </section>
  );
}
